import type { UntypedFunction } from '@mnd-frontend/ts';
import { Button, GREY_400, Icons, NEUTRAL_WHITE, media } from '@mnd-frontend/ui';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Panel = styled.div<{ $show: boolean }>`
  display: none;
  ${media.greaterThan('mobile')} {
    display: flex;
    background: ${NEUTRAL_WHITE};
    position: fixed;
    right: 0;
    transform: translateX(${({ $show }) => ($show ? '0' : '23rem')});
    width: 22.5rem;
    top: 0;
    bottom: 0;
    overflow: hidden;
    z-index: 9999;
    justify-content: space-between;
    flex-direction: column;
    transition: all 500ms;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid ${GREY_400};

  button:hover {
    background: ${NEUTRAL_WHITE};
  }
`;

const PanelFooter = styled.div`
  padding: 2rem 1.5rem;
  border-top: 1px solid ${GREY_400};
`;

const PanelBody = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`;

const Backdrop = styled.div<{ $show: boolean }>`
  background: rgba(0, 0, 0, 0.75);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  opacity: ${props => (props.$show ? 1 : 0)};
  transition: opacity 0.25s;
`;

const SidePanel: React.FC<{
  isOpen: boolean;
  onClose: UntypedFunction;
  header: React.ReactNode;
  footer: React.ReactNode;
  children?: React.ReactNode;
}> = ({ isOpen, onClose, children, header, footer }) => {
  const { t } = useTranslation();
  const [showSidebar, setShowSidebar] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setShowSidebar(true));
      document.body.classList.add('block-scrolling');
      return () => {
        document.body.classList.remove('block-scrolling');
      };
    }
    return;
  }, [isOpen]);

  const hidePanel = useCallback(() => {
    setShowSidebar(false);
    setTimeout(onClose, 300);
  }, [onClose]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        hidePanel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hidePanel]);

  return isOpen ? (
    <Backdrop
      $show={showSidebar}
      onClick={e => {
        if (e.target === e.currentTarget) {
          hidePanel();
        }
      }}
    >
      <Panel $show={showSidebar}>
        <PanelHeader>
          <div>{header}</div>
          <Button
            type="button"
            configuration="text"
            onClick={hidePanel}
            icon={<Icons.Close />}
            aria-label={t('common_close')}
          />
        </PanelHeader>
        <PanelBody>{children}</PanelBody>
        <PanelFooter>{footer}</PanelFooter>
      </Panel>
    </Backdrop>
  ) : null;
};

export default SidePanel;
